import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { LEARN_DECK_EXIT_DURATION_MS } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import {
  useIsLearnMapOpen,
  useLearnCardsPathname,
  useLearnMapModeSelector,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { LearnSummaryPresenceContext, type LearnSummaryPresence } from "#learn-summary-presence";
import { LearnSummarySurface } from "#learn-summary-surface";
import { isLearnSummaryPathname } from "#use-learn-summary-map-home-exit";
import { useEffect, useMemo, useState } from "react";

/**
 * Renders Learn's summary above the persistent deck; the summary route itself is only a history
 * entry, like the deck's. Owning the summary here, rather than in the route, is what lets Start be
 * instant: Start replaces the route with the deck at once, so the deck is the screen and takes
 * touches from the press on, while this host keeps the departing summary on top (touch-transparent)
 * until its card has flown off. It then unmounts it on the exit animation's own clock; nothing
 * waits for that, and nothing reads the animation.
 *
 * Each visit to the summary route is a fresh summary, as each mount of the route was.
 *
 * Once the cards have been requested, beneath the map home they are the deck's next-card preview,
 * not the summary card: leaving the home opens the deck on that card, so it is the card that slides
 * in. The summary stays unmounted there while the deck has a card to preview; with none (the day's
 * limit, say), the summary card is what the cards show.
 */
export function LearnSummaryHost() {
  // Learn's map opens over the cards: the summary stays where it was beneath it, and leaves and
  // returns with them.
  const pathname = useLearnCardsPathname();
  const isMapOpen = useIsLearnMapOpen();
  const isMapHome = useLearnMapModeSelector((mode) => mode.isMapHome) ?? false;
  const [hasSummaryPreview] = useContextSelectors(LearnLayoutContext, (c) => c.hasSummaryPreview);
  // The map provider supplies the remembered cards route, even while `/learn` enters the map.
  const isSummaryRoute = isLearnSummaryPathname(pathname);
  const isLearnFocused = useIsFocused();
  const [visit, setVisit] = useState({ id: 0, isSummaryRoute });
  if (visit.isSummaryRoute !== isSummaryRoute) {
    setVisit({ id: isSummaryRoute ? visit.id + 1 : visit.id, isSummaryRoute });
  }
  const visitId = visit.id;
  const [departure, setDeparture] = useState<{ at: number; visitId: number } | null>(null);
  const isDeparting = departure?.visitId === visitId;
  const departedAt = isDeparting ? departure.at : null;
  const isDepartureOnly = departedAt !== null && !isSummaryRoute;
  useEffect(() => {
    if (!isDepartureOnly) {
      return;
    }
    // The flight started on the press; opening the deck may have taken some of it. Unmount what
    // is no longer visible once the flight's own duration has passed since then.
    const remaining = Math.max(0, LEARN_DECK_EXIT_DURATION_MS - (Date.now() - departedAt));
    const timer = setTimeout(setDeparture, remaining, null);
    return () => clearTimeout(timer);
  }, [departedAt, isDepartureOnly]);
  const presence = useMemo<LearnSummaryPresence>(
    () => ({
      depart: () => setDeparture({ at: Date.now(), visitId }),
      isCurrent: isSummaryRoute && isLearnFocused && !isMapOpen,
      isDeparting,
      isUnderMap: isSummaryRoute && isMapOpen,
      stay: () => setDeparture((current) => (current?.visitId === visitId ? null : current)),
    }),
    [isDeparting, isLearnFocused, isMapOpen, isSummaryRoute, visitId],
  );
  // Away from the summary, nothing is mounted: it holds no queries while the deck runs.
  const isShown = (isSummaryRoute && !(isMapHome && hasSummaryPreview)) || isDeparting;
  return (
    <>
      {isShown && (
        <View
          accessibilityElementsHidden={isDepartureOnly}
          // Departing, the summary draws above the deck's live surface (also z-10, but earlier).
          className={isDepartureOnly ? "absolute inset-0 z-10" : "absolute inset-0"}
          importantForAccessibility={isDepartureOnly ? "no-hide-descendants" : "auto"}
          pointerEvents={isDepartureOnly ? "none" : "box-none"}
          testID={isDepartureOnly ? "learn-summary-departing" : "learn-summary-host"}
        >
          <LearnSummaryPresenceContext value={presence}>
            <LearnSummarySurface key={visitId} />
          </LearnSummaryPresenceContext>
        </View>
      )}
    </>
  );
}
